import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, downloadReportPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Loader2 } from "lucide-react";

export default function ReportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/reports/${id}`)
      .then((r) => setReport(r.data))
      .catch(() => setError(true));
  }, [id]);

  if (error)
    return (
      <div className="p-8 text-center text-slate-500" data-testid="report-not-found">
        Report not found. <button className="text-indigo-600 underline" onClick={() => navigate("/")}>Back to dashboard</button>
      </div>
    );
  if (!report)
    return (
      <div className="p-10 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading report…
      </div>
    );

  const data = report.data || {};

  return (
    <div className="p-6 lg:p-8" data-testid="report-view">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <FileText className="w-5 h-5" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{report.title}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Generated {data.generated_at ? new Date(data.generated_at).toLocaleString("en-GB") : "—"} · demonstration data
            </p>
          </div>
        </div>
        <Button
          onClick={() => downloadReportPdf(id, report.title).catch(() => toast.error("PDF download failed"))}
          data-testid="report-download-pdf"
          className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 shrink-0"
        >
          <Download className="w-4 h-4" /> Download PDF
        </Button>
      </div>

      {report.summary && (
        <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap mb-6">
          {report.summary}
        </div>
      )}

      <div className="space-y-6">
        {(data.sections || []).map((sec, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid={`report-section-${i}`}>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{sec.title}</h2>

            {sec.metrics?.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {sec.metrics.map((m, j) => (
                  <div key={j} className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide truncate">{m.label}</div>
                    <div className="text-xl font-bold text-slate-900 mt-1">
                      {typeof m.value === "number" ? m.value.toLocaleString() : m.value}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {sec.breakdown?.length > 0 && (
              <div className="mb-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                      <th className="py-2">Type</th>
                      <th className="py-2 text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sec.breakdown.map((b, j) => (
                      <tr key={j} className="border-b border-slate-50">
                        <td className="py-2 text-slate-700">{b.label}</td>
                        <td className="py-2 text-right font-medium text-slate-900">{b.value.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {sec.items?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                      <th className="py-2 pr-4">Title</th>
                      <th className="py-2 pr-4">Category</th>
                      <th className="py-2 text-right">Total</th>
                      <th className="py-2 text-right">Available</th>
                      <th className="py-2 text-right">On Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sec.items.map((it, j) => (
                      <tr key={j} className="border-b border-slate-50">
                        <td className="py-2 pr-4 text-slate-800 font-medium">{it.title}</td>
                        <td className="py-2 pr-4 text-slate-500">{it.category || "—"}</td>
                        <td className="py-2 text-right">{it.total_stock}</td>
                        <td className="py-2 text-right">{it.available}</td>
                        <td className="py-2 text-right">{it.on_order}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
